import { Injectable, OnDestroy } from "@angular/core";
import { MsalService } from "@azure/msal-angular";
import { BehaviorSubject } from "rxjs";
import { environment } from "src/environments/environment";
import { nullSafe } from "../utils";
import { AppUser } from "./api.gen";
import { LoaderService } from "./loader.service";

@Injectable({
  providedIn: "root",
})
export class AuthService implements OnDestroy {
  private currentUserSubject: BehaviorSubject<AppUser>;
  constructor(private msalService: MsalService, private loader: LoaderService) {
    this.currentUserSubject = new BehaviorSubject<AppUser>(
      this.getAppUserFromStorage()
    );
  }

  public clearUserInfo() {
    localStorage.removeItem("user_info");
    this.currentUserSubject.next(null);
  }

  public setAppUser(appUser: AppUser) {
    if (appUser) {
      localStorage.setItem("user_info", JSON.stringify(appUser));
      this.currentUserSubject.next(appUser);
    }
  }

  private getAppUserFromStorage(): AppUser {
    const text = localStorage.getItem("user_info");
    if (text) {
      return JSON.parse(text);
    }
    return null;
  }

  public get ActiveUser(): AppUser {
    const user = this.currentUserSubject.value;
    if (user) {
      user.displayName = this.msalService.instance.getActiveAccount().name;
    }

    return user;
  }

  public get AdUserName() {
    return nullSafe(
      () => this.msalService.instance.getActiveAccount().name,
      ""
    );
  }

  login() {
    const isIE = true; // window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;

    if (isIE) {
      this.msalService.loginRedirect();
    } else {
      this.msalService.loginPopup();
    }
  }

  logout() {
    this.loader.show();
    // remove user from local storage to log user out
    this.clearUserInfo();
    this.msalService.logout();
    this.loader.clear();
  }

  get isAuthenticated(): boolean {
    const result =
      this.isAuthSuccessfull &&
      nullSafe(() => this.ActiveUser.employeeId, -1) > 0;
    return result;
  }

  get isAuthSuccessfull(): boolean {
    return !!this.msalService.instance.getActiveAccount();
  }

  checkUserRole(claim: string) {
    if (this.ActiveUser) {
      const userClaims = nullSafe(() => this.ActiveUser.modules, []);
      return userClaims.indexOf(claim) >= 0;
    }
    return false;
  }

  hasClaim(claim: string) {
    if (this.ActiveUser) {
      const userClaims = nullSafe(() => this.ActiveUser.features, []);
      return userClaims.indexOf(claim) >= 0;
    }
    return false;
  }

  get ActiveUserClaims(): string[] {
    const userModules = nullSafe(() => this.ActiveUser.modules, []);
    const userFeatures = nullSafe(() => this.ActiveUser.features, []);
    const userClaims = userModules.concat(userFeatures);

    return userClaims;
  }

  isAuthorised(claimsIdentifier: string | string[]) {
    const claims = this.getClaimInfos(claimsIdentifier);
    const userClaims = this.ActiveUserClaims;
    if (userClaims.length === 0 || claims.length === 0) {
      return false;
    }

    const reqClaims = claims.filter((c) => c.isRequired === true);
    if (reqClaims.length > 0) {
      return reqClaims.every((c) => userClaims.indexOf(c.claim) >= 0);
    }

    const optionalClaims = claims.filter((c) => c.isRequired === false);
    if (optionalClaims.length > 0) {
      return optionalClaims.some((c) => userClaims.indexOf(c.claim) >= 0);
    }

    return false;
  }

  getClaimInfos(claimsIdentifier: string | string[]): IClaimInfo[] {
    const claimList: IClaimInfo[] = [];
    if (Array.isArray(claimsIdentifier)) {
      claimsIdentifier.forEach((element) => {
        const claimDetail = element.trim().split(":");
        const claimInfo: IClaimInfo = {
          claim: claimDetail[0],
          isRequired: false,
        };
        if (claimDetail.length >= 2) {
          if (
            claimDetail[1].trim() === "true" ||
            claimDetail[1].trim() === "required"
          ) {
            claimInfo.isRequired = true;
          }
        }
        claimList.push(claimInfo);
      });
    } else {
      const claimDetail = claimsIdentifier.trim().split(":");
      const claimInfo: IClaimInfo = {
        claim: claimDetail[0],
        isRequired: false,
      };
      claimList.push(claimInfo);
    }

    return claimList;
  }

  isActiveFeature(moduleOrFeatureId: string): boolean {
    return (
      nullSafe(
        () =>
          'm-rmt,m-ptc,m-imm,m-ext,m-cw'
            .split(",")
            .filter(
              (f) =>
                f.toLowerCase().trim() ===
                moduleOrFeatureId.toLowerCase().trim()
            ).length,
        -1
      ) > 0
    );
  }

  hasAcceptedPolicy() {
    if (this.ActiveUser) {
      return this.checkPolicyAccepted();
    }
  }
  checkPolicyAccepted(): boolean {
    const policy = this.ActiveUser.additionalInfo;
    if (policy) {
      return policy.immPolicyAccepted;
    } else {
      return false;
    }
  }

  ngOnDestroy(): void {
    //Logger.log('AuthService destroy');
    //this.broadcastService.getMSALSubject().next(1);
    //if (this.loginFail) {
    //  this.loginFail.unsubscribe();
    //}
    //if (this.tokenfail) {
    //  this.tokenfail.unsubscribe();
    //}
  }
}

export interface IClaimInfo {
  claim: string;
  isRequired: boolean;
}
